import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import type { CrawlRunError } from '../crawlRuns';
import { formatDate } from '../format';
import { StatusBadge } from './StatusBadge';

// The drill-down behind a crawl run's "Errors: N" count (task crawl_error_detail).
// Lazily loads GET /v1/admin/manuals/crawl-runs/:id/errors and lists each counted
// failure — the stage that broke, the message (with any provider HTTP status),
// the model in play, and the candidate URL — so an operator can troubleshoot WHY
// a run errored. Rendered only while the count is expanded, so the fetch happens
// on demand, not for every run in the table. Shared by Manual review (and any
// other surface that shows a run) so the error vocabulary stays in one place.

interface ErrorsResponse {
  errors: CrawlRunError[];
  truncated: boolean;
}

// search/fetch/model → a short, plain-language gloss of what the stage was, so
// the operator doesn't have to know the crawl's internals. Unknown kinds (a
// future stage) fall back to the raw label rather than mislabeling.
function kindLabel(kind: string): string {
  switch (kind) {
    case 'search':
      return 'Web search';
    case 'fetch':
      return 'PDF fetch';
    case 'model':
      return 'Model';
    default:
      return kind;
  }
}

export function CrawlRunErrors({ runId }: { runId: string }): React.ReactElement {
  const [data, setData] = useState<ErrorsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setData(null);
    setError(null);
    adminApi<ErrorsResponse>(`/v1/admin/manuals/crawl-runs/${runId}/errors`)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load error details.');
      });
    return () => {
      ignore = true;
    };
  }, [runId]);

  if (error !== null) {
    return <div className="admin-auth-banner error">{error}</div>;
  }
  if (data === null) {
    return <p className="admin-loading-row">Loading error details…</p>;
  }
  if (data.errors.length === 0) {
    // A run whose error_count predates this capture (migration 0031) has no rows.
    // Say so rather than implying a clean run — the count above is still real.
    return (
      <p className="admin-empty">
        No error detail was recorded for this run. It may predate error capture, or the failures
        were logged without detail.
      </p>
    );
  }

  return (
    <div className="crawl-error-detail">
      <div className="scroll-x">
        <table className="admin-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Stage</th>
              <th>Model</th>
              <th>What happened</th>
            </tr>
          </thead>
          <tbody>
            {data.errors.map((e) => (
              <tr key={e.id}>
                <td>{formatDate(e.created_at)}</td>
                <td>
                  <StatusBadge status="manual" label={kindLabel(e.kind)} />
                </td>
                <td>
                  {e.device_model_id !== null ? (
                    <Link to={`/admin/devices/${e.device_model_id}`}>
                      {e.model_label ?? 'Model'}
                    </Link>
                  ) : (
                    (e.model_label ?? '—')
                  )}
                </td>
                <td>
                  <div className="crawl-error-message">
                    {e.message}
                    {e.http_status !== null ? (
                      <span className="crawl-error-status"> (HTTP {e.http_status})</span>
                    ) : null}
                  </div>
                  {e.url !== null && e.url.length > 0 ? (
                    <a
                      className="crawl-error-url"
                      href={e.url}
                      target="_blank"
                      rel="noreferrer"
                      title={e.url}
                    >
                      {e.url}
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.truncated ? (
        <p className="admin-defs-note">
          Showing the first {data.errors.length} errors for this run; older ones are not listed.
        </p>
      ) : null}
    </div>
  );
}
